import { createContext, FC, PropsWithChildren, useContext, useState } from 'react';
import noop from 'utils/noop';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import {
  defaultState as paginationDefaultState,
  PaginationContextState,
} from 'utils/contexts/pagination';
import { SortingContextState, defaultState as sortingDefaultState } from 'utils/contexts/sorting';
import { ensureEnum } from 'utils/tsHelpers';
import { ListReference, ListReferenceSortableProperty, CreatorAndModifierById } from '../types';
import {
  FETCH_REFERENCES_TRIGGER,
  SORTABLE_TABLE_COLUMNS_TO_PROPERTIES,
  TABLE_COLUMNS,
} from '../components/segmentationResults/referencesInsights/constants';
import { useCreatorsAndModifiersQuery, useListId, useListReferencesQuery } from '../hooks';

// Context for tracking values relevant for the list references table part of the page
type ListReferencesState = {
  isLoading: boolean;
  isError: boolean;
  listReferences: ListReference[];
  totalCount: number;
  creatorsAndModifiersById: CreatorAndModifierById;
  fetch: (trigger: FETCH_REFERENCES_TRIGGER) => void;
  fetchTrigger: FETCH_REFERENCES_TRIGGER;
  fetchCreatorsAndModifiers: () => void;
  hasReferencesBeenFetched: boolean;
} & PaginationContextState &
  SortingContextState;

const defaultListReferencesState = {
  isLoading: false,
  isError: false,
  listReferences: [],
  totalCount: 0,
  fetch: noop,
  fetchTrigger: FETCH_REFERENCES_TRIGGER.INSIGHTS,
  fetchCreatorsAndModifiers: noop,
  hasReferencesBeenFetched: false,
  creatorsAndModifiersById: {},
  ...paginationDefaultState,
  ...sortingDefaultState,
};

export const ListReferencesContext = createContext<ListReferencesState>(defaultListReferencesState);

export const useListReferencesContext = () => useContext(ListReferencesContext);

export const ListReferencesContextProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // Sorting
  const [sortBy, setSortBy] = useState<string>(ListReferenceSortableProperty.name);
  const [sortDirection, setSortDirection] = useState<SORT_DIRECTIONS>(SORT_DIRECTIONS.DESCENDING);

  // References
  const listId = useListId();
  const { creatorsAndModifiersById, fetch: fetchCreatorsAndModifiers } =
    useCreatorsAndModifiersQuery();
  const sortedTableColumnId = ensureEnum(sortBy, TABLE_COLUMNS, TABLE_COLUMNS.NAME);
  const sortedProperty = ensureEnum(
    SORTABLE_TABLE_COLUMNS_TO_PROPERTIES[sortedTableColumnId],
    ListReferenceSortableProperty,
    ListReferenceSortableProperty.name,
  );

  const [hasReferencesBeenFetched, setHasReferencesBeenFetched] = useState<boolean>(false);
  const [fetchTrigger, setFetchTrigger] = useState<FETCH_REFERENCES_TRIGGER>(
    FETCH_REFERENCES_TRIGGER.INSIGHTS,
  );

  const { hasError, isLoading, fetch, results, totalCount } = useListReferencesQuery({
    currentPage,
    listId,
    pageSize,
    sortDirection,
    sortedProperty,
  });

  const handleFetch = (trigger: FETCH_REFERENCES_TRIGGER) => {
    setFetchTrigger(trigger);
    if (!hasReferencesBeenFetched) {
      fetch();
      setHasReferencesBeenFetched(true);
    }
  };

  const value = {
    isLoading,
    isError: hasError,
    listReferences: results,
    totalCount,
    creatorsAndModifiersById,
    fetch: handleFetch,
    fetchTrigger,
    fetchCreatorsAndModifiers,
    hasReferencesBeenFetched,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
  };

  return <ListReferencesContext.Provider value={value}>{children}</ListReferencesContext.Provider>;
};
